import { useState } from "react";
import { useT } from "../../i18n/index.js";
import { formatMoney } from "../../lib/format-fr.js";

/**
 * F-expertise-check (CEO 2026-09-22) — conferência do RAPPORT DE EXPERTISE
 * contra o valor a faturar, antes de finalizar o carro.
 *
 * "Ao finalizar o carro, o técnico sênior ou o gerente do chantier precisam
 *  verificar se a fatura corresponde ao mesmo valor do rapport de expertise…
 *  Enquanto não fizer isso, o portal deve mostrar um alarme: carro a
 *  finalizar."
 *
 * A tela mostra o valor a faturar (a fatura emitida ou, antes dela, o devis) e
 * pede o valor do rapport. Se baterem, um toque confirma. Se não baterem, a
 * conferência ainda pode ser gravada — com o motivo, que o escritório lê antes
 * de faturar. Fatura acima do rapport a seguradora não paga.
 */

export type ExpertiseState = {
  inScope: boolean;
  hasDoc: boolean;
  amount: number | null;
  checkedAt: string | Date | null;
  checkedByName: string | null;
  match: boolean | null;
  note: string | null;
  invoiceHt: number | null;
  invoiceTtc: number | null;
  invoiceSource: "invoice" | "devis" | null;
  invoiceNumber: string | null;
  pending: boolean;
};

export function ExpertiseCheckCard({
  expertise: e,
  /**
   * O trabalho acabou? A cobrança é "AO FINALIZAR o carro" (CEO): num carro
   * recém-recebido, que ninguém assumiu ainda, o card só atrapalharia — foi o
   * que aconteceu com o Fiat 500 FX-651-JG, recebido e já exibindo "carro a
   * finalizar". A conferência JÁ FEITA continua aparecendo sempre (o resumo
   * verde), para quem abrir o carro depois saber quem conferiu.
   */
  finished,
  saving,
  error,
  onConfirm,
}: {
  expertise: ExpertiseState;
  finished: boolean;
  saving: boolean;
  error: string | null;
  onConfirm: (p: { expertiseAmount: number; match: boolean; note?: string }) => void;
}) {
  const t = useT();
  const [valor, setValor] = useState<string>(e.amount != null ? String(e.amount) : "");
  const [motivo, setMotivo] = useState<string>(e.note ?? "");

  // Carro fora do escopo (anterior à regra) não mostra nada: o CEO pediu
  // explicitamente para não gerar trabalho retroativo.
  if (!e.inScope) return null;

  if (e.checkedAt == null && !finished) return null;

  if (e.checkedAt != null) {
    const quando = new Date(e.checkedAt);
    return (
      <div className="rounded-lg border border-lime/30 bg-lime/5 px-3 py-2 text-xs">
        <p className="font-semibold text-lime">
          ✓ {t("chantier.expTitle")}
          {e.match === false && (
            <span className="ml-1 text-amber-300">· {t("chantier.expDiffBadge")}</span>
          )}
        </p>
        <p className="text-neutral-300">
          {e.amount != null && `${formatMoney(e.amount, { decimals: 2 })} · `}
          {e.checkedByName ?? "—"} · {quando.toLocaleDateString()}
        </p>
        {e.note && <p className="text-xs text-amber-200">⚠ {e.note}</p>}
      </div>
    );
  }

  const valorNum = valor.trim() === "" ? null : Number(valor.replace(",", "."));
  const base = e.invoiceHt;
  const diferenca = valorNum != null && base != null ? valorNum - base : null;
  const bate = diferenca != null && Math.abs(diferenca) < 0.01;
  const podeGravar =
    e.hasDoc && valorNum != null && valorNum > 0 && (bate || motivo.trim().length >= 3);

  return (
    <div className="space-y-2 rounded-lg border border-amber-500/50 bg-amber-500/10 p-3">
      <p className="text-sm font-bold text-amber-200">⚠ {t("chantier.expAlarmTitle")}</p>
      <p className="text-xs text-neutral-300">{t("chantier.expHint")}</p>

      <div className="flex items-center justify-between gap-2 rounded border border-neutral-700 bg-neutral-950/60 px-2.5 py-1.5 text-xs">
        <span className="text-neutral-400">
          {e.invoiceSource === "invoice"
            ? `${t("chantier.expInvoiceLabel")}${e.invoiceNumber ? ` ${e.invoiceNumber}` : ""}`
            : t("chantier.expDevisLabel")}
        </span>
        <span className="font-mono font-semibold">
          {base != null ? `${formatMoney(base, { decimals: 2 })} HT` : "—"}
        </span>
      </div>

      {!e.hasDoc && (
        <p className="rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-xs font-semibold text-amber-200">
          📎 {t("chantier.expNeedDoc")}
        </p>
      )}

      <label className="block text-xs text-neutral-300">
        {t("chantier.expAmountLabel")}
        <input
          type="number"
          inputMode="decimal"
          min={0}
          step="0.01"
          value={valor}
          onChange={(ev) => setValor(ev.target.value)}
          placeholder="0,00"
          className="mt-1 w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2.5 text-sm text-neutral-100"
        />
      </label>

      {diferenca != null &&
        (bate ? (
          <p className="text-xs font-semibold text-lime">✓ {t("chantier.expMatch")}</p>
        ) : (
          <>
            <p className="text-xs font-semibold text-amber-200">
              ⚠ {t("chantier.expDiffPre")} {formatMoney(Math.abs(diferenca), { decimals: 2 })}{" "}
              {diferenca > 0 ? t("chantier.expDiffOver") : t("chantier.expDiffUnder")}
            </p>
            <textarea
              value={motivo}
              onChange={(ev) => setMotivo(ev.target.value)}
              rows={2}
              placeholder={t("chantier.expNotePh")}
              className="w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm text-neutral-100"
            />
          </>
        ))}

      <button
        type="button"
        disabled={!podeGravar || saving}
        onClick={() =>
          onConfirm({
            expertiseAmount: valorNum!,
            match: bate,
            ...(motivo.trim() ? { note: motivo.trim() } : {}),
          })
        }
        className="w-full rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-bold text-neutral-900 disabled:opacity-40"
      >
        {saving ? t("chantier.expSaving") : `✔ ${t("chantier.expConfirmBtn")}`}
      </button>
      {error && (
        <p className="rounded-md bg-red-100 px-3 py-2 text-xs font-semibold text-red-700">
          ✕ {error}
        </p>
      )}
    </div>
  );
}
